import React, { useState } from 'react';
import Plot from 'react-plotly.js';
import { Card, Row, Col, Spinner, Alert, Form, Button } from 'react-bootstrap';
import {
  useGetBatchListQuery,
  useLazyGetBatchAnalysisQuery,
  useLazyDownloadTemplateQuery,
  useUploadBatchFileMutation,
} from '../../store/api/analysisApi';
import { BIOMARKERS } from '../../config';

const BatchExplorer: React.FC = () => {
  const [selectedBatch, setSelectedBatch] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const [distBiomarker, setDistBiomarker] = useState<string>(BIOMARKERS[0]);
  const [distScale, setDistScale] = useState<'raw' | 'log'>('raw');

  const {
    data: batchList = [],
    isFetching: loadingBatchList,
    refetch: refetchBatchList,
  } = useGetBatchListQuery();

  const [triggerBatchAnalysis, { data: analysisData, isFetching: loadingAnalysis }] =
    useLazyGetBatchAnalysisQuery();

  const [uploadBatchFile, { isLoading: loading }] = useUploadBatchFileMutation();
  const [triggerDownloadTemplate] = useLazyDownloadTemplateQuery();

  const handleAnalyze = async () => {
    if (!selectedBatch) return;
    setError(null);
    try {
      await triggerBatchAnalysis(selectedBatch).unwrap();
    } catch (err) {
      setError("Failed to analyze batch.");
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setError(null);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;
    setError(null);
    try {
      await uploadBatchFile(selectedFile).unwrap();
      setSelectedFile(null);
      setFileInputKey((k) => k + 1);
      refetchBatchList();
      alert('File uploaded successfully! Batch list has been updated.');
    } catch (err: any) {
      setError('File upload failed.');
    }
  };

  const handleDownloadTemplate = async () => {
    setError(null);
    try {
      const blob = await triggerDownloadTemplate().unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'biomarker_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Template download failed.');
    }
  };

  const getDistributionData = (): number[] => {
    if (!analysisData || !analysisData.rawData || !analysisData.rawData[distBiomarker]) return [];
    const rawData = analysisData.rawData[distBiomarker];
    const numericData = rawData.filter((v: any) => typeof v === 'number' && !isNaN(v));
    if (distScale === 'log') {
      return numericData.filter((v: number) => v > 0).map(Math.log);
    }
    return numericData;
  };

  return (
    <Card>
      <Card.Body>
        <Card.Title>Batch Explorer & Upload</Card.Title>
        <Row className="align-items-end g-3">
          <Col md={4}>
            <Form.Group><Form.Label>Select a Saved Batch</Form.Label>
              <Form.Select value={selectedBatch} onChange={e => setSelectedBatch(e.target.value)}>
                <option value="">Select a batch...</option>
                {batchList.map(b => <option key={b} value={b}>{b}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2} className="d-grid">
            <Button onClick={handleAnalyze} disabled={!selectedBatch || loadingAnalysis}>
              {loadingAnalysis ? <Spinner as="span" animation="border" size="sm" /> : 'Analyze'}
            </Button>
          </Col>
          <Col md={4}>
            <Form.Group><Form.Label>Or, Upload a New Batch</Form.Label>
              <div className="d-flex">
                <Form.Control
                  key={fileInputKey}
                  type="file"
                  onChange={handleFileSelect}
                  accept=".csv,.xlsx"
                  disabled={loading}
                />
                <Button
                  onClick={handleFileUpload}
                  className="ms-2"
                  style={{ whiteSpace: 'nowrap' }}
                  disabled={!selectedFile || loading}
                >
                  {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Envoyer'}
                </Button>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col className="d-flex justify-content-end">
            <Button
              onClick={handleDownloadTemplate}
              variant="outline-success"
              style={{ whiteSpace: 'nowrap' }}
              disabled={loading}
            >
              DL Template
            </Button>
          </Col>
        </Row>
        
        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
        {(loading || loadingBatchList) && <div className="text-center p-5"><Spinner animation="border" /></div>}

        {analysisData && (
          <div className="mt-4">
            <hr />
            <h3>Analysis for: {selectedBatch}</h3>
            <Row>
              <Col><Card bg="light" className="text-center"><Card.Body><h5>{analysisData.metrics.subjectCount}</h5><p className="mb-0">Subjects</p></Card.Body></Card></Col>
              <Col><Card bg="light" className="text-center"><Card.Body><h5>{analysisData.metrics.missingValues}</h5><p className="mb-0">Missing Values</p></Card.Body></Card></Col>
              <Col><Card bg="light" className="text-center"><Card.Body><h5>{analysisData.metrics.meanAge?.toFixed(1) || 'N/A'}</h5><p className="mb-0">Mean Age</p></Card.Body></Card></Col>
              <Col><Card bg="light" className="text-center"><Card.Body><h5>{analysisData.metrics.meanBmi?.toFixed(1) || 'N/A'}</h5><p className="mb-0">Mean BMI</p></Card.Body></Card></Col>
            </Row>

            <Card className="mt-4">
              <Card.Header as="h5">Interactive Distribution Explorer</Card.Header>
              <Card.Body>
                <Row className="align-items-center mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Select Biomarker</Form.Label>
                      <Form.Select value={distBiomarker} onChange={e => setDistBiomarker(e.target.value)}>
                        {BIOMARKERS.map(b => <option key={b} value={b}>{b}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Select Scale</Form.Label>
                      <div>
                        <Form.Check type="radio" name="scale" label="Raw" checked={distScale === 'raw'} onChange={() => setDistScale('raw')} inline />
                        <Form.Check type="radio" name="scale" label="Log" checked={distScale === 'log'} onChange={() => setDistScale('log')} inline />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Plot
                  data={[{
                    x: getDistributionData(),
                    type: 'histogram',
                    marker: { color: '#0A6353' },
                    autobinx: true 
                  }]}
                  layout={{ 
                    title: { text: `Distribution for ${distBiomarker} (${distScale} scale)`}, 
                    height: 400, 
                    xaxis: {title: {text: distBiomarker}}, 
                    yaxis: {title: {text: 'Count'}}
                  }}
                  style={{ width: '100%' }} useResizeHandler={true}
                />
              </Card.Body>
            </Card>

            <Row className="mt-3">
              <Col md={6}>
                <Plot 
                  data={[{ ...analysisData.plots.correlationHeatmap, type: 'heatmap', colorscale: 'Viridis'}]}
                  layout={{ title: { text: 'Biomarker Correlation Heatmap' }, height: 500, yaxis: { autorange: 'reversed' } }}
                  style={{ width: '100%' }} useResizeHandler={true}
                />
              </Col>
              <Col md={6}>
                <Plot
                  data={[
                    { x: analysisData.plots.batchPca.globalScores.pc1, y: analysisData.plots.batchPca.globalScores.pc2, mode: 'markers', name: 'Global Population', marker: { color: '#cccccc', size: 5, opacity: 0.5 }},
                    { x: analysisData.plots.batchPca.batchScores.pc1, y: analysisData.plots.batchPca.batchScores.pc2, mode: 'markers', name: 'Selected Batch', marker: { color: '#0A6353', size: 8 }}
                  ]}
                  layout={{
                    title: { text: 'Batch PCA vs Global Population' }, height: 500,
                    xaxis: { title: { text: `PC1 (${(analysisData.plots.batchPca.varianceExplained[0] * 100).toFixed(1)}%)`}},
                    yaxis: { title: { text: `PC2 (${(analysisData.plots.batchPca.varianceExplained[1] * 100).toFixed(1)}%)`}},
                    legend: { x: 0.01, y: 0.99 }
                  }}
                  style={{ width: '100%' }} useResizeHandler={true}
                />
              </Col>
            </Row>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default BatchExplorer;